/**
 * One token, one price, every screen.
 *
 * Measured on the operator's wallet with both screens open, one token, the same
 * cost basis on each:
 *
 *   Portfolio    value 0.4331   PnL +0.0331 (+8.23%)
 *   Token page   value 0.428    PnL +0.028  (+7.14%)
 *
 * A 1.2% gap on a real position — and neither number was wrong for its own
 * source, which is exactly what made it dangerous. The portfolio started from
 * canon's market snapshot, the pair page from the pool spot it read at load,
 * and both then advanced only on trades. For a token that had not traded since
 * those reads, they stayed two different last-known prices indefinitely.
 *
 * So "last known price" stops being per-screen. Everyone who learns a price
 * publishes it here — the trade tape, the pair's live state, canon when a
 * portfolio answer arrives — the freshest wins, and every screen reads the same
 * number.
 *
 * Across TABS too: the operator's acceptance is a portfolio and a token page
 * side by side, which is two JavaScript contexts. A module-level map would
 * agree with itself and still show him two numbers.
 */

export type LivePriceEntry = {
  /** Quote-unit price (GRAM per token). */
  priceTon: number
  /** USD price at the same moment; null when the source did not carry one. */
  priceUsd: number | null
  /**
   * Wall-clock ms this knowledge is current to — ONE clock for every source,
   * and the reason this is not the price's own timestamp.
   *
   * It used to be: a tick published the trade's CHAIN seconds and a snapshot
   * published the browser's wall clock. Those are different clocks and the
   * chain one always lags — block time, then indexing, then delivery. So a
   * canon read that landed at wall T made every tick describing a trade before
   * T look older, and the store refused the very tick carrying the new price.
   * On a quiet token that is not a flicker; it is the price standing still
   * until something else trades.
   */
  knownAsOfMs: number
  /**
   * Chain seconds the price describes, when the source knows them. Only ever
   * compared against another chain stamp — it is what stops a tape tick
   * delivered out of order from overwriting a newer trade.
   */
  chainAt?: number
  /** The token's OWN USD-per-GRAM rate, carried forward so a later quote-only
      price can still move USD. Never a platform average — that is how two
      units end up describing different moments. */
  impliedUsdPerTon?: number
}

export type LivePriceInput = {
  priceTon: number
  priceUsd: number | null
  /**
   * When this source's knowledge is current to, in wall-clock ms. Live sources
   * omit it — they just heard the price. A canon read passes the moment its
   * REQUEST was issued: its answer cannot contain anything that happened after
   * that, so it must not overwrite a tick that arrived while it was in flight.
   */
  knownAsOfMs?: number
  /** Chain seconds the price describes, for sources that know them. */
  chainAt?: number | null
}

/**
 * A ceiling on the map. Every trade on the platform is published here — that is
 * deliberate, it is how a token bought a second ago already has a price — but a
 * tab left open on a busy tape would otherwise keep an entry per token ever
 * traded. Eviction is oldest-inserted-first, and only above the cap.
 */
const MAX_TOKENS = 4000

const CHANNEL_NAME = 'wils.live-prices'

const prices = new Map<string, LivePriceEntry>()
/** Listeners are told WHICH token moved. Every trade on the platform passes
    through this store, and a consumer that watches eight holdings must not
    re-render for the other thousands — the portfolio provider sits above the
    router, so one spurious notification re-renders every trading surface. */
const listeners = new Set<(token: string) => void>()

const resetHooks = new Set<() => void>()

let channel: BroadcastChannel | null = null
function bus(): BroadcastChannel | null {
  if (channel) return channel
  if (typeof BroadcastChannel === 'undefined') return null
  channel = new BroadcastChannel(CHANNEL_NAME)
  channel.onmessage = (event: MessageEvent) => {
    const data = event.data as { token?: unknown; price?: LivePriceInput } | null
    if (typeof data?.token !== 'string' || !data.price) return
    // A price from another tab is applied through the SAME freshness rule as a
    // local one — a tab with a stale read must not win by shouting last.
    apply(data.token, data.price)
  }
  return channel
}

function usable(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  return Number.isFinite(value) && value > 0 ? value : null
}

function apply(token: string, input: LivePriceInput): boolean {
  const priceTon = usable(input.priceTon)
  if (priceTon === null) return false
  const knownAsOfMs = Number.isFinite(input.knownAsOfMs as number)
    ? (input.knownAsOfMs as number)
    : Date.now()
  const chainAt = Number.isFinite(input.chainAt as number) ? (input.chainAt as number) : undefined
  const previous = prices.get(token)
  if (previous) {
    // Two chain stamps are comparable, and a strictly older trade never wins.
    // This is the rule a tape tick delivered out of order runs into.
    if (chainAt !== undefined && previous.chainAt !== undefined && chainAt < previous.chainAt) {
      return false
    }
    // A snapshot (canon) has no chain moment. It must not replace a price that
    // already describes one — the pair page's USD, or a tape tick. Login
    // refetch issues a later request time and would otherwise flash the
    // restated platform rate over the pair spot for a second. Measured on the
    // operator's open EAGLE tab after the session dropped: PnL jumped a
    // fraction of a percent, then came back.
    if (chainAt === undefined && previous.chainAt !== undefined) {
      return false
    }
    // Otherwise the more RECENT knowledge wins. A source that knew less
    // recently cannot correct one that knew more recently, whatever moment the
    // price it carries happens to describe.
    if (knownAsOfMs < previous.knownAsOfMs) return false
  }

  const priceUsd = usable(input.priceUsd)
  const impliedUsdPerTon = priceUsd !== null ? priceUsd / priceTon : previous?.impliedUsdPerTon
  const next: LivePriceEntry = {
    priceTon,
    priceUsd,
    knownAsOfMs,
    // A source with no chain stamp inherits the last one instead of erasing it.
    // Erasing disarms the out-of-order guard for the NEXT tick, however old the
    // trade it carries — and a canon read at time R reflects a chain moment at
    // least as new as whatever was stored, so carrying it forward understates
    // nothing.
    chainAt: chainAt ?? previous?.chainAt,
    impliedUsdPerTon,
  }
  if (
    previous &&
    previous.priceTon === next.priceTon &&
    previous.priceUsd === next.priceUsd &&
    previous.impliedUsdPerTon === next.impliedUsdPerTon
  ) {
    // The price did not move. Keep the entry as it is so nothing re-renders
    // for a trade that changed nothing.
    return false
  }
  prices.set(token, next)
  evictOverflow()
  for (const listener of listeners) listener(token)
  return true
}

/**
 * Drops the longest-standing entries once the map is over its ceiling.
 *
 * Insertion order, not a sort. Sorting 4000 entries on every new token would
 * put an O(n log n) back into the per-tick path this cap exists to keep cheap —
 * and for an overflow guard "oldest inserted" is close enough to "least
 * useful". A token that still matters is re-published by its next tick, or by
 * the canon read that owns the position.
 */
function evictOverflow(): void {
  while (prices.size > MAX_TOKENS) {
    const oldest = prices.keys().next()
    if (oldest.done) return
    prices.delete(oldest.value)
  }
}

/**
 * Publish a price learned from any source.
 *
 * Live sources pass the price alone. A source answering from a snapshot passes
 * `knownAsOfMs` — when its knowledge was taken — so it cannot overwrite
 * something the store learned since.
 */
export function publishLivePrice(token: string, input: LivePriceInput): void {
  if (!token) return
  if (!apply(token, input)) return
  // Only a price that actually moved is worth another tab's attention.
  bus()?.postMessage({ token, price: prices.get(token) })
}

export function getLivePrice(token: string): LivePriceEntry | undefined {
  return prices.get(token)
}

/**
 * The USD half of a stored price, by the one rule every screen must use.
 *
 * A price is a pair of numbers, and two screens agreeing on the GRAM half while
 * deriving USD differently is the same defect in a smaller font. So this lives
 * with the store, not with either consumer: the tick's own USD when it carried
 * one, otherwise the token's OWN implied rate. Never a platform average.
 */
/**
 * Remaining USD at the SAME TON/USD as the live token price.
 *
 * Canon restates remaining at the platform TON/USD (handler.go:514). Pair
 * `price_usd` uses the last trade's own rate. Subtracting one from a value
 * priced at the other is how SFAILURE showed +0.6% USD next to 0 GRAM — the
 * token did not move, the two rates did. Both screens use this so a flat TON
 * position is flat in USD too.
 */
export function restatedRemainingUsd(
  remainingTon: number | null | undefined,
  priceTon: number | null | undefined,
  priceUsd: number | null | undefined
): number | null {
  if (remainingTon === null || remainingTon === undefined || !Number.isFinite(remainingTon)) {
    return null
  }
  if (remainingTon < 0) return null
  const ton = usable(priceTon)
  const usd = usable(priceUsd)
  if (ton === null || usd === null) return null
  const value = remainingTon * (usd / ton)
  return Number.isFinite(value) ? value : null
}

export function livePriceUsd(
  entry: Pick<LivePriceEntry, 'priceTon' | 'priceUsd' | 'impliedUsdPerTon'> | undefined
): number | null {
  if (!entry) return null
  const direct = usable(entry.priceUsd)
  if (direct !== null) return direct
  const priceTon = usable(entry.priceTon)
  const rate = usable(entry.impliedUsdPerTon)
  if (priceTon === null || rate === null) return null
  const value = priceTon * rate
  return Number.isFinite(value) ? value : null
}

/**
 * The price to value a position at: the store's, or the caller's own fallback
 * when the store has never heard of this token.
 *
 * All-or-nothing per source, deliberately. Taking the GRAM half from one source
 * and the USD half from another puts two different moments in one row, which is
 * how the same position once showed -1% and -0.5% at the same time.
 */
/**
 * The pair screen's own REST already has the spot. A store entry without a
 * chain stamp is canon's restated USD — using it here is the one-frame flash
 * after login, before the pair republishes.
 */
export function chainBackedLivePrice(
  entry: LivePriceEntry | undefined
): LivePriceEntry | undefined {
  return entry?.chainAt !== undefined ? entry : undefined
}

export function resolvePositionPrice(
  entry: LivePriceEntry | undefined,
  fallbackTon: number | null,
  fallbackUsd: number | null
): { priceTon: number | null; priceUsd: number | null } {
  const priceTon = usable(entry?.priceTon)
  if (priceTon === null) return { priceTon: fallbackTon, priceUsd: fallbackUsd }
  const priceUsd = livePriceUsd(entry)
  if (priceUsd !== null) return { priceTon, priceUsd }
  // The entry has no USD of its own and no rate to carry — a first tick that
  // came without `price_usd`, before any source published one. The caller's
  // fallback is the SAME token's pair, so its USD-per-GRAM rate converts this
  // price without borrowing anyone else's moment. Taking `fallbackUsd` whole
  // would do exactly that; this does not.
  const rateTon = usable(fallbackTon)
  const rateUsd = usable(fallbackUsd)
  if (rateTon === null || rateUsd === null) return { priceTon, priceUsd: null }
  const converted = priceTon * (rateUsd / rateTon)
  return { priceTon, priceUsd: Number.isFinite(converted) ? converted : null }
}

export function subscribeLivePrices(listener: (token: string) => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test seam: the store is module state, and the bus outlives a test file. */
export function resetLivePriceStoreForTest(): void {
  prices.clear()
  listeners.clear()
  channel?.close()
  channel = null
  bus()
  for (const reset of resetHooks) reset()
}

/**
 * Registers module state that is only meaningful ALONGSIDE this store, so one
 * reset clears both.
 *
 * The unpriced diagnostic is the case: it reads this store and keeps its own
 * once-per-state ledger with a ceiling. Left un-reset, one test file's
 * sightings spend another's budget inside the same worker and the second file
 * fails on an empty array for no reason it can see. A registry rather than an
 * import, so the dependency stays one-way — the store must not know what reads
 * it.
 */
export function onLivePriceStoreReset(reset: () => void): void {
  resetHooks.add(reset)
}

// Listening starts at import, not at the first publish or subscribe. A tab that
// only READS — the portfolio, sitting still while the other tab drives the pair
// page — would otherwise never open the bus and never hear the price it is
// supposed to agree with.
bus()
